import React, { useState } from 'react';
import { 
  CheckCircle, XCircle, Clock, Send, Users, FileText, 
  ArrowDown, RotateCw, Award, Briefcase, UserCheck, TrendingUp
} from 'lucide-react';

const CandidateJourneyFlow = () => {
  const [journeyType, setJourneyType] = useState('success'); // 'success' or 'rejection'

  // Successful Journey Path
  const successJourney = [
    {
      stage: 1,
      candidateStatus: { 
        status: 'Sourced', 
        icon: '🔍', 
        color: 'purple', 
        description: 'Profile created in system' 
      },
      submissionStatus: { 
        status: 'N/A', 
        icon: '—', 
        color: 'gray', 
        description: 'Not yet submitted' 
      },
      duration: '1-2 days'
    },
    {
      stage: 2,
      candidateStatus: { 
        status: 'Screened', 
        icon: '✅', 
        color: 'purple', 
        description: 'Initial screening passed' 
      },
      submissionStatus: { 
        status: 'N/A', 
        icon: '—', 
        color: 'gray', 
        description: 'Not yet submitted' 
      },
      duration: '2-3 days'
    },
    {
      stage: 3,
      candidateStatus: { 
        status: 'Available', 
        icon: '🟢', 
        color: 'green', 
        description: 'Ready for job submissions' 
      },
      submissionStatus: { 
        status: 'N/A', 
        icon: '—', 
        color: 'gray', 
        description: 'Not yet submitted' 
      },
      duration: 'Varies'
    },
    {
      stage: 4,
      candidateStatus: { 
        status: 'Submitted', 
        icon: '📤', 
        color: 'blue', 
        description: 'Submitted to client' 
      },
      submissionStatus: { 
        status: 'Submitted', 
        icon: '📋', 
        color: 'blue', 
        description: 'Awaiting client review',
        sync: true
      },
      duration: '1-3 days'
    },
    {
      stage: 5,
      candidateStatus: { 
        status: 'Interview Scheduled', 
        icon: '📅', 
        color: 'blue', 
        description: 'Interview in progress' 
      },
      submissionStatus: { 
        status: 'Interview Scheduled', 
        icon: '🎤', 
        color: 'blue', 
        description: 'Client interviewing',
        sync: true
      },
      duration: '3-5 days'
    },
    {
      stage: 6,
      candidateStatus: { 
        status: 'Offer Made', 
        icon: '💼', 
        color: 'green', 
        description: 'Offer extended to candidate' 
      },
      submissionStatus: { 
        status: 'Offer Made', 
        icon: '🎁', 
        color: 'green', 
        description: 'Client made offer',
        sync: true
      },
      duration: '2-5 days'
    },
    {
      stage: 7,
      candidateStatus: { 
        status: 'Offer Accepted', 
        icon: '🎉', 
        color: 'green', 
        description: 'Candidate accepted offer' 
      },
      submissionStatus: { 
        status: 'Offer Accepted', 
        icon: '✔️', 
        color: 'green', 
        description: 'Placement confirmed',
        sync: true
      },
      duration: '1-2 days'
    },
    {
      stage: 8,
      candidateStatus: { 
        status: 'Onboarded', 
        icon: '🏆', 
        color: 'green', 
        description: 'Successfully placed' 
      },
      submissionStatus: { 
        status: 'Placed', 
        icon: '🌟', 
        color: 'green', 
        description: 'Candidate started work',
        sync: true
      },
      duration: 'Complete'
    }
  ];

  // Rejection Journey Path
  const rejectionJourney = [
    {
      stage: 1,
      candidateStatus: { 
        status: 'Available', 
        icon: '🟢', 
        color: 'green', 
        description: 'Ready for job submissions' 
      },
      submissionStatus: { 
        status: 'N/A', 
        icon: '—', 
        color: 'gray', 
        description: 'Not yet submitted' 
      },
      duration: 'Varies'
    },
    {
      stage: 2,
      candidateStatus: { 
        status: 'Submitted', 
        icon: '📤', 
        color: 'blue', 
        description: 'Submitted to client' 
      },
      submissionStatus: { 
        status: 'Submitted', 
        icon: '📋', 
        color: 'blue', 
        description: 'Awaiting client review',
        sync: true
      },
      duration: '1-3 days'
    },
    {
      stage: 3,
      candidateStatus: { 
        status: 'Interview Scheduled', 
        icon: '📅', 
        color: 'blue', 
        description: 'Interview in progress' 
      },
      submissionStatus: { 
        status: 'Interview Scheduled', 
        icon: '🎤', 
        color: 'blue', 
        description: 'Client interviewing',
        sync: true
      },
      duration: '3-5 days'
    },
    {
      stage: 4,
      candidateStatus: { 
        status: 'Available', 
        icon: '🔄', 
        color: 'orange', 
        description: 'Back to available pool' 
      },
      submissionStatus: { 
        status: 'Client Rejected', 
        icon: '❌', 
        color: 'red', 
        description: 'Not selected by client',
        sync: true
      },
      duration: '1 day'
    },
    {
      stage: 5,
      candidateStatus: { 
        status: 'Available', 
        icon: '🟢', 
        color: 'green', 
        description: 'Ready for new opportunities' 
      },
      submissionStatus: { 
        status: 'Closed', 
        icon: '🔒', 
        color: 'gray', 
        description: 'Submission archived' 
      },
      duration: 'New cycle begins'
    }
  ];

  const currentJourney = journeyType === 'success' ? successJourney : rejectionJourney;

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-100 border-purple-400 text-purple-900',
      blue: 'bg-blue-100 border-blue-400 text-blue-900',
      green: 'bg-green-100 border-green-400 text-green-900',
      orange: 'bg-orange-100 border-orange-400 text-orange-900',
      red: 'bg-red-100 border-red-400 text-red-900',
      gray: 'bg-gray-100 border-gray-300 text-gray-600'
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      {/* Header with Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              Candidate Journey Visualization
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Parallel status transitions: Candidate Status (left) ↔ Submission Status (right)
            </p>
          </div>
          
          {/* Journey Type Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setJourneyType('success')}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
                journeyType === 'success'
                  ? 'bg-green-500 text-white shadow-md'
                  : 'bg-transparent text-gray-600 hover:bg-gray-200'
              }`}
            >
              ✅ Successful Journey
            </button>
            <button
              onClick={() => setJourneyType('rejection')}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
                journeyType === 'rejection'
                  ? 'bg-red-500 text-white shadow-md'
                  : 'bg-transparent text-gray-600 hover:bg-gray-200'
              }`}
            >
              ❌ Rejection Journey
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-medium text-gray-700">Candidate Status</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-700">Submission Status</span>
          </div>
          <div className="flex items-center gap-2">
            <RotateCw className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-gray-700">Auto-Sync Point</span>
          </div>
        </div>
      </div>

      {/* Vertical Journey Flow */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col items-center space-y-4">
          {currentJourney.map((step, index) => (
            <React.Fragment key={step.stage}>
              {/* Stage Row */}
              <div className="w-full">
                {/* Stage Number */}
                <div className="flex justify-center mb-3">
                  <div className="bg-gray-200 text-gray-700 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm">
                    {step.stage}
                  </div>
                </div>

                {/* Parallel Status Cards */}
                <div className="grid grid-cols-2 gap-8">
                  {/* Left Side - Candidate Status */}
                  <div className="flex justify-end">
                    <div className={`relative border-2 rounded-lg p-4 w-80 ${getColorClasses(step.candidateStatus.color)}`}>
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{step.candidateStatus.icon}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Users className="h-4 w-4" />
                            <span className="font-bold text-sm">CANDIDATE STATUS</span>
                          </div>
                          <div className="font-semibold text-lg">{step.candidateStatus.status}</div>
                          <div className="text-xs mt-1 opacity-80">{step.candidateStatus.description}</div>
                        </div>
                      </div>
                      {/* Sync Indicator */}
                      {step.submissionStatus.sync && (
                        <div className="absolute -right-10 top-1/2 transform -translate-y-1/2">
                          <RotateCw className="h-6 w-6 text-green-600 animate-spin" style={{ animationDuration: '3s' }} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Side - Submission Status */}
                  <div className="flex justify-start">
                    <div className={`border-2 rounded-lg p-4 w-80 ${getColorClasses(step.submissionStatus.color)}`}>
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{step.submissionStatus.icon}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4" />
                            <span className="font-bold text-sm">SUBMISSION STATUS</span>
                          </div>
                          <div className="font-semibold text-lg">{step.submissionStatus.status}</div>
                          <div className="text-xs mt-1 opacity-80">{step.submissionStatus.description}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Duration Badge */}
                <div className="flex justify-center mt-3">
                  <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {step.duration}
                  </div>
                </div>
              </div>

              {/* Vertical Arrow (except after last item) */}
              {index < currentJourney.length - 1 && (
                <div className="flex justify-center py-2">
                  <ArrowDown className="h-6 w-6 text-gray-400" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Summary */}
        <div className={`mt-8 p-4 rounded-lg border-2 ${
          journeyType === 'success' 
            ? 'bg-green-50 border-green-300' 
            : 'bg-orange-50 border-orange-300'
        }`}>
          <div className="flex items-start gap-3">
            {journeyType === 'success' ? (
              <>
                <Award className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-green-900 mb-1">Successful Journey Complete</h4>
                  <p className="text-sm text-green-800">
                    Candidate progressed through all stages and was successfully placed. 
                    Both candidate and submission statuses synchronized throughout the process, 
                    culminating in a successful onboarding.
                  </p>
                </div>
              </>
            ) : (
              <>
                <RotateCw className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-orange-900 mb-1">Candidate Returns to Pool</h4>
                  <p className="text-sm text-orange-800">
                    After client rejection, candidate status automatically reverts to "Available" 
                    while submission status is marked as "Client Rejected." Candidate is now ready 
                    for new job opportunities.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateJourneyFlow;
